import Box from '@mui/material/Box';
import { Button } from '@openx/components-core/lib/Button/Button';
import { useLoaderContext } from '@openx/components-core/lib/Loader/LoaderContext';
import { GenerateForecastButton } from '@openx/components-deal/lib/components/DealForecast/components/GenerateForecastButton';
import { DealForecast } from '@openx/components-deal/lib/components/DealForecast/DealForecast';
import { useDealFormContext } from '@openx/components-deal/lib/DealForm/DealForm.context';
import { useDealFormStyles } from '@openx/components-deal/lib/DealForm/hooks/useDealFormStyles';
import { CuratedDealFeesFormSection } from '@openx/components-deal/lib/DealForm/sections/CuratedDealFeesSection/CuratedDealFeesFormSection';
import { DealFloorAndDurationFormSection } from '@openx/components-deal/lib/DealForm/sections/DealFloorAndDurationFormSection';
import { DealPackageSection } from '@openx/components-deal/lib/DealForm/sections/DealPackageSection/DealPackageSection';
import { DiscountsFormSection } from '@openx/components-deal/lib/DealForm/sections/DiscountsFormSection';
import { EnabledDataPartnersFormSection } from '@openx/components-deal/lib/DealForm/sections/EnabledDataPartnersFormSection';
import { FrequencyCappingSection } from '@openx/components-deal/lib/DealForm/sections/FrequencyCappingSection/FrequencyCappingSection';
import { IdsFormSection } from '@openx/components-deal/lib/DealForm/sections/IdsFormSection';
import { OnBehalfOfFormSection } from '@openx/components-deal/lib/DealForm/sections/OnBehalfOfFormSection';
import { PacingSection } from '@openx/components-deal/lib/DealForm/sections/PacingSection/PacingSection';
import { ResultsByOpenX } from '@openx/components-deal/lib/DealForm/sections/ResultsByOpenX';
import { ResultsByOpenXSection } from '@openx/components-deal/lib/DealForm/sections/ResultsByOpenXSection/ResultsByOpenXSection';
import { keysToExcludeFromPayload } from '@openx/components-deal/lib/DealForm/utils/constants';
import { useDealContext } from '@openx/components-deal/lib/utils/context/DealContext';
import { useIsForecastingEnabled } from '@openx/components-deal/lib/utils/hooks/dealForecast/useIsForecastingEnabled';
import { useShowRboLightweight } from '@openx/components-deal/lib/utils/useShowRboLightweight';
import { OPENX_AGENT_TOOL } from '@openx/components-openx-agent/components/Home/utils/consts';
import { ToolCallButton } from '@openx/components-openx-agent/components/ToolCallButton/ToolCallButton';
import { useInventoryQualityProps } from '@openx/components-package/components/PackageForm/useInventoryQualityProps';
import type { InventoryQualityValue } from '@openx/components-package/components/PackageForm/useIqScoringAutoCheck';
import { ALLOWLIST_DEAL_PACING } from '@openx/constants/feature-flag-keys';
import { ACL_TYPE } from '@openx/types/aclType';
import { type DealFormDataWithTargeting, dealFormField } from '@openx/types/deal';
import { Feature_Flags_Enum } from '@openx/types/gqlTypes';
import { InventoryFormatTypeOption } from '@openx/types/targeting/formatType';
import { TargetingFields } from '@openx/types/targeting/targeting';
import { usePermissionContext } from '@openx/utils/permission/context/PermissionContext';
import { isAllowedByCompiledAcl } from '@openx/utils/permission/validators';
import { AQUA_RTB_DEMAND_PARTNER_ID } from '@openx/utils/services/config';
import isEqual from 'lodash/isEqual';
import omit from 'lodash/omit';
import { type JSX, useCallback, useMemo, useRef, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams } from 'react-router';
import { useAuth } from '../../../components/AuthContext/context';
import { useSetContextualMaxPom } from '../../../configs/deals/utils/useSetContextualMaxPom';
import { useIsAllowed } from '../../../configs/permission/utils/useIsAllowed';
import { isAdmin, isInternalUser } from '../../../permissions/validators';
import { useFeatureFlag } from '../../featureFlags/useFeatureFlag';
import { DealDetails } from './DealDetails';

type DealFormFieldsProps = {
  isEdit: boolean;
  isAllowFrequencyCapping?: boolean;
};

export const DealFormFields = ({ isEdit, isAllowFrequencyCapping = false }: DealFormFieldsProps): JSX.Element => {
  const { session } = useAuth();
  const { dealInitialValues, pckgIsInitialLoading } = useDealFormContext();
  const hasCuratedDealBuyerDiscountFeature = useFeatureFlag(Feature_Flags_Enum.CuratedDealBuyerDiscount);
  const locationState = useLocation().state;
  const { isAllowed } = useIsAllowed();

  const {
    formState: { isSubmitting },
  } = useFormContext<DealFormDataWithTargeting>();

  const [hasAudienceError, setHasAudienceError] = useState(false);

  const navigate = useNavigate();
  const { dealUid } = useParams();

  const { t } = useTranslation();
  const observableAccountUid = useWatch({ name: dealFormField.ACCOUNT_UID });
  const observableThirdPartyFeesConfigEnabled = useWatch<DealFormDataWithTargeting, 'third_party_fees_config_enabled'>({
    name: 'third_party_fees_config_enabled',
  });

  const formValues = useWatch<DealFormDataWithTargeting>();

  const formValuesToCompare = omit(formValues, keysToExcludeFromPayload);
  const dealInitialValuesToCompare = omit(dealInitialValues, keysToExcludeFromPayload);

  const isFormUnchanged = isEqual(formValuesToCompare, dealInitialValuesToCompare);

  // Frequency Capping is not supported by Aqua DSP - RTB, so it has to stay blocked while that DSP is selected
  const isAquaDspSelected = useMemo(
    () =>
      !!formValues?.deal_participants?.some(participant => participant?.demand_partner === AQUA_RTB_DEMAND_PARTNER_ID),
    [formValues?.deal_participants]
  );

  const { isPub } = usePermissionContext();
  const isForecastingEnabled = useIsForecastingEnabled();
  const watchedTargeting = formValues?.targeting?.targeting;
  const inventoryFormat = watchedTargeting?.[TargetingFields.INVENTORY_FORMAT];
  const iqAdPlacement = inventoryFormat?.[InventoryFormatTypeOption.AD_PLACEMENT]?.val;
  const iqDistributionChannel = inventoryFormat?.[InventoryFormatTypeOption.DISTRIBUTION_CHANNEL]?.val;
  const iqInventoryQuality = (watchedTargeting?.[TargetingFields.INVENTORY_QUALITY] ?? null) as InventoryQualityValue;

  const iqCheckboxProps = useInventoryQualityProps({
    isPub,
    adPlacement: iqAdPlacement,
    distributionChannel: iqDistributionChannel,
    inventoryQuality: iqInventoryQuality,
  });

  const { isGrowthbookFeatureOn } = usePermissionContext();
  const isPacingVisible = isGrowthbookFeatureOn(ALLOWLIST_DEAL_PACING);

  const isInternalAdmin = isAdmin(session) && isInternalUser(session);

  const observableMonetizingUid = useWatch({ name: dealFormField.MONETIZING_UID });
  const { useMonetizingFetch } = useDealContext();
  const { isLoading: isMonetizingLoading } = useMonetizingFetch(observableMonetizingUid);

  const { isContextualFeePending } = useSetContextualMaxPom();

  const isDisabled =
    isSubmitting ||
    isContextualFeePending ||
    !!pckgIsInitialLoading ||
    (isEdit && isFormUnchanged) ||
    hasAudienceError ||
    !!iqCheckboxProps?.error ||
    (isEdit && isMonetizingLoading);

  const hasOldImpressionGoal = !!dealInitialValues?.lifetime_impression_goal;
  const showRboLightweight = useShowRboLightweight();

  const forecastContainerRef = useRef<HTMLDivElement>(null);
  const styles = useDealFormStyles(isForecastingEnabled, forecastContainerRef);
  const observableCurrency = useWatch({ name: dealFormField.CURRENCY });

  const { useSetMultiproviderEnabledDataPartner } = useDealContext();

  const { hasMultiProviderPricingData, pricingInfo, loadingAudienceCheck } = useSetMultiproviderEnabledDataPartner();
  const { loaderCount } = useLoaderContext();

  const cancelButtonLabel = locationState?.dsInitialDealFormPreset
    ? t('Cancel and return to Deal Prompt')
    : t('Cancel');

  const onCancelClick = useCallback(() => {
    if (!isEdit && locationState?.dsInitialDealFormPreset) {
      return navigate('/deal-prompt');
    }

    if (isEdit && dealUid) {
      return navigate(`/deals/${dealUid}`);
    }

    return navigate('/deals');
  }, [isEdit, locationState?.dsInitialDealFormPreset, dealUid, navigate]);

  return (
    <Box sx={styles.container} data-test="deal-form-fields">
      <Box sx={styles.mainContent}>
        <IdsFormSection isEdit={isEdit} />
        {isInternalAdmin && <OnBehalfOfFormSection isEdit={isEdit} />}
        <DealDetails isEdit={isEdit} onAudienceValidationChange={setHasAudienceError} />
        {isAllowFrequencyCapping && (
          <FrequencyCappingSection
            isDisabled={isAquaDspSelected}
            disabledInfo={t('Frequency Capping is not available for the selected DSP.')}
          />
        )}
        <PacingSection isEdit={isEdit} ignoreDealType isHidden={!isPacingVisible} isLoading={isMonetizingLoading} />
        <DealFloorAndDurationFormSection isEdit={isEdit} renderDates={hasOldImpressionGoal} />
        {showRboLightweight ? <ResultsByOpenXSection isEdit={isEdit} /> : <ResultsByOpenX isEdit={isEdit} />}
        <CuratedDealFeesFormSection key={`${observableAccountUid}-oaa-fees`} isEdit={isEdit} />
        {hasCuratedDealBuyerDiscountFeature && observableThirdPartyFeesConfigEnabled && <DiscountsFormSection />}

        <EnabledDataPartnersFormSection
          pricingInfo={pricingInfo}
          loadingAudienceCheck={loadingAudienceCheck}
          hasMultiProviderPricingData={hasMultiProviderPricingData}
          isAdmin={isInternalAdmin}
        />

        <DealPackageSection currency={observableCurrency || ''} sessionAccountName={session?.account?.name} />
      </Box>

      {isForecastingEnabled && (
        <Box ref={forecastContainerRef} sx={styles.forecastContainer}>
          <DealForecast />
        </Box>
      )}

      <Box sx={styles.bottomBar}>
        <Button
          type="submit"
          color="primary"
          loading={isSubmitting}
          disabled={!!loaderCount || isDisabled}
          data-test="submit-button"
        >
          {t('Save')}
        </Button>

        <ToolCallButton
          isAllowed={isAllowed(isAllowedByCompiledAcl(ACL_TYPE.XPERT_ENABLED))}
          isLoading={isSubmitting}
          toolId={OPENX_AGENT_TOOL.TARGETING_EXPLAINER}
          toolData={formValues}
          dataTest="check-targeting"
          name={t('Check Targeting')}
        />

        <Button variant="text" onClick={onCancelClick} data-test="cancel-button">
          {cancelButtonLabel}
        </Button>
      </Box>
      {isForecastingEnabled && (
        <Box sx={styles.forecastButtonContainer}>
          <GenerateForecastButton />
        </Box>
      )}
    </Box>
  );
};
